import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import {
  Rocket,
  Clock,
  MapPin,
  ShieldCheck,
  CheckCircle,
  Truck,
  MoveRight,
  Package,
  Zap,
  Route,
  Warehouse,
  ArrowRight,
  ClipboardCheck,
  Navigation,
  Headphones,
  CheckCircle2
} from 'lucide-react'

import expressImage from '../assets/images/data/express.png'
import CTA from './CTA'
import IndustryExpertise from './IndustryExpertise'
import MoreServicesSection from './MoreServicesSection'

const services = [
  {
    number: '01',
    icon: Zap,
    title: 'Same-Day Delivery',
    description:
      'Time-sensitive shipments delivered within the same day in selected service locations.'
  },
  {
    number: '02',
    icon: Clock,
    title: 'Next-Day Delivery',
    description:
      'Reliable next-day delivery solutions designed to keep your business moving.'
  },
  {
    number: '03',
    icon: Package,
    title: 'Priority Parcel Delivery',
    description:
      'Priority handling for important documents, parcels and urgent consignments.'
  },
  {
    number: '04',
    icon: Route,
    title: 'Express Distribution',
    description:
      'Optimized delivery routes and streamlined distribution for faster movement.'
  },
  {
    number: '05',
    icon: Warehouse,
    title: 'Business Logistics',
    description:
      'Flexible express shipping support for business, retail and commercial requirements.'
  },
  {
    number: '06',
    icon: Navigation,
    title: 'Shipment Tracking',
    description:
      'Stay informed with shipment tracking and delivery milestone updates.'
  }
]

const benefits = [
  {
    icon: Rocket,
    title: 'Speed & Priority',
    description:
      'Priority shipment handling and optimized movement for time-sensitive deliveries.'
  },
  {
    icon: ShieldCheck,
    title: 'Secure Handling',
    description:
      'Careful shipment handling throughout the pickup, transit and delivery journey.'
  },
  {
    icon: MapPin,
    title: 'Wide Connectivity',
    description:
      'Express logistics support across metro zones and selected service locations.'
  },
  {
    icon: Headphones,
    title: 'Customer Support',
    description:
      'Dedicated assistance to help businesses manage their express shipment requirements.'
  }
]

const process = [
  {
    number: '01',
    icon: ClipboardCheck,
    title: 'Shipment Booking',
    description:
      'Share your shipment details and choose the express delivery service that suits your requirements.'
  },
  {
    number: '02',
    icon: Package,
    title: 'Pickup & Processing',
    description:
      'Your shipment is collected, processed and prepared for priority movement.'
  },
  {
    number: '03',
    icon: Truck,
    title: 'Express Transportation',
    description:
      'The shipment moves through an optimized logistics network with tracking updates.'
  },
  {
    number: '04',
    icon: CheckCircle,
    title: 'Final Delivery',
    description:
      'Your shipment reaches its destination with delivery status updates.'
  }
]

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
}

const sectionAnimation = {
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, amount: 0.15 },
  variants: fadeUp,
  transition: { duration: 0.6 }
}

export default function ExpressDeliveryPage () {
  return (
    <>
      <Helmet>
        <title>Express Delivery Services | S Infinite Express</title>

        <meta
          name='description'
          content='Explore express delivery services by S Infinite Express. Get priority shipment handling, same-day and next-day delivery options in selected locations, and convenient shipment tracking.'
        />

        <meta
          name='keywords'
          content='Express Delivery, Same-day delivery, Next-day delivery, Express courier services India, Fast logistics, Urgent parcel delivery, Priority shipment, S Infinite Express, Express shipping, Business courier services'
        />

        <meta name='robots' content='index, follow' />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/services/express-delivery'
        />

        <meta
          property='og:title'
          content='Express Delivery Services | S Infinite Express'
        />

        <meta
          property='og:description'
          content='Move urgent shipments with S Infinite Express. Explore priority handling, express transportation and convenient shipment tracking.'
        />

        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/services/express-delivery'
        />

        <meta property='og:type' content='website' />
      </Helmet>

      <main className='overflow-hidden bg-white'>
        {/* HERO SECTION */}

        <section className='relative overflow-hidden bg-gradient-to-br from-white via-blue-50/40 to-white'>
          <div className='max-w-7xl mx-auto px-5 sm:px-8 lg:px-6 py-8 md:py-12'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center'>
              {/* LEFT CONTENT */}

              <motion.div {...sectionAnimation} className='space-y-6'>
                <h1 className='text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-[#211f57] leading-tight'>
                  Express Delivery
                  <span className='text-[#d32c30]'> Services</span>
                </h1>

                <div className='w-24 h-1.5 bg-[#d32c30] rounded-full' />

                <p className='text-gray-600 text-base sm:text-lg leading-relaxed'>
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>{' '}
                  provides reliable, efficient and priority express delivery
                  solutions for businesses and individuals. We help move
                  important documents, urgent parcels and business consignments
                  through coordinated logistics operations.
                </p>

                <p className='text-gray-600 leading-relaxed'>
                  From same-day and next-day delivery options to priority parcel
                  transportation, our express logistics services focus on
                  shipment handling, efficient coordination and convenient
                  delivery visibility.
                </p>

                {/* ACTION BUTTONS */}

                <div className='flex flex-col sm:flex-row gap-4 pt-3'>
                  <a
                    href='/get-a-quote'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#d32c30] text-white font-semibold rounded-xl shadow-lg hover:bg-[#b82629] hover:-translate-y-1 transition-all duration-300'
                  >
                    Get a Shipping Quote
                    <MoveRight className='w-5 h-5' />
                  </a>

                  <a
                    href='/track-shipment'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 border-2 border-[#211f57] text-[#211f57] font-semibold rounded-xl hover:bg-[#211f57] hover:text-white transition-all duration-300'
                  >
                    Track Shipment
                    <ArrowRight className='w-5 h-5' />
                  </a>
                </div>

                {/* TRUST POINTS */}

                <div className='flex flex-wrap gap-x-6 gap-y-3 pt-5 text-sm font-medium text-gray-600'>
                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Priority Handling
                  </span>

                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Express Connectivity
                  </span>

                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Shipment Visibility
                  </span>
                </div>
              </motion.div>

              {/* RIGHT IMAGE */}

              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -top-6 -right-5 w-32 h-32 bg-[#fff44f]/60 rounded-full blur-3xl' />
                <div className='absolute -bottom-6 -left-5 w-32 h-32 bg-blue-200/70 rounded-full blur-3xl' />

                <div className='relative p-2 bg-white rounded-3xl shadow-2xl border border-gray-100'>
                  <img
                    src={expressImage}
                    alt='S Infinite Express priority delivery transportation'
                    className='w-full h-[200px] sm:h-[300px] lg:h-[420px] object-cover rounded-2xl'
                    loading='lazy'
                    decoding='async'
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* INTRODUCTION SECTION */}

        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-8 sm:py-10 lg:py-12'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />
          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12'>
            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16'>
              {/* LEFT CONTENT */}

              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className='space-y-6'
              >
                <h2 className='text-2xl font-bold leading-tight text-[#211f57] sm:text-2xl lg:text-3xl'>
                  Moving Your Business Forward Through
                  <span className='text-[#d32c30]'>
                    {' '}
                    Faster Express Delivery
                  </span>
                </h2>

                <p className='text-base leading-relaxed text-gray-600 sm:text-lg'>
                  At{' '}
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>
                  , we understand that timely deliveries play an important role
                  in business operations. Our express logistics network helps
                  businesses manage urgent shipments with priority handling and
                  coordinated transportation.
                </p>

                <p className='leading-relaxed text-gray-600'>
                  Whether you are sending important documents, individual
                  parcels or commercial consignments, our express delivery
                  solutions focus on efficient movement and dependable shipment
                  coordination.
                </p>

                {/* KEY FEATURES */}

                <div className='grid grid-cols-1 gap-x-5 gap-y-4 pt-3 sm:grid-cols-2'>
                  {[
                    'Same-Day & Next-Day Options',
                    'Priority Shipment Handling',
                    'Express Distribution Network',
                    'Shipment Tracking Updates'
                  ].map((item, index) => (
                    <div key={index} className='flex items-center gap-3'>
                      <CheckCircle2
                        size={20}
                        className='shrink-0 text-green-600'
                      />
                      <span className='text-sm font-semibold text-[#211f57]'>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* RIGHT CAPABILITIES CARD */}

              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -right-5 -top-6 h-32 w-32 rounded-full bg-[#fff44f]/50 blur-3xl' />
                <div className='absolute -bottom-6 -left-5 h-32 w-32 rounded-full bg-blue-200/70 blur-3xl' />

                <div className='relative overflow-hidden rounded-3xl bg-[#211f57] p-3 shadow-2xl sm:p-4 lg:p-5'>
                  <div className='absolute -right-16 -top-16 h-48 w-48 rounded-full border-[25px] border-white/5' />
                  <div className='absolute -bottom-20 -right-10 h-56 w-56 rounded-full border-[25px] border-white/5' />

                  <div className='relative z-10'>
                    <div className='mb-2 flex items-center gap-4'>
                      <span className='text-xs font-bold uppercase tracking-[2px] text-[#fff44f]'>
                        Our Capabilities
                      </span>
                    </div>

                    <p className='mb-4 text-sm leading-7 text-white/70 sm:text-base'>
                      Priority express delivery solutions designed to simplify
                      urgent shipment movement, improve coordination and support
                      your business requirements.
                    </p>

                    <div className='space-y-2'>
                      {[
                        {
                          icon: Zap,
                          title: 'Priority Express Delivery',
                          desc: 'Faster shipment movement for urgent delivery requirements.'
                        },
                        {
                          icon: Package,
                          title: 'Parcel & Document Delivery',
                          desc: 'Convenient transportation for important documents and parcels.'
                        },
                        {
                          icon: Route,
                          title: 'Express Distribution',
                          desc: 'Organized delivery routes and streamlined distribution.'
                        },
                        {
                          icon: Navigation,
                          title: 'Shipment Coordination',
                          desc: 'Tracking visibility and coordinated delivery operations.'
                        }
                      ].map((item, index) => {
                        return (
                          <div
                            key={index}
                            className='flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-all duration-300 hover:bg-white/10'
                          >
                            <div>
                              <h4 className='text-sm font-bold text-white sm:text-base'>
                                {item.title}
                              </h4>

                              <p className='mt-1 text-xs leading-5 text-white/55 sm:text-sm'>
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* EXPRESS SERVICES */}

        <section className='py-10 bg-[#f7f8fc]'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='max-w-4xl mx-auto text-center mb-5'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                What We Offer
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57] mt-2'>
                Our Express Delivery Services
              </h2>

              <p className='text-gray-500 leading-7 mt-2'>
                Fast, flexible logistics for urgent shipments and business
                deliveries.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {services.map((service, index) => {
                const Icon = service.icon

                return (
                  <motion.div
                    key={service.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.08
                    }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='flex justify-between items-center'>
                      <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                        <Icon size={26} />
                      </div>

                      <span className='text-3xl font-black text-gray-100 group-hover:text-[#d32c30]/20 transition-colors'>
                        {service.number}
                      </span>
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-3'>
                      {service.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {service.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* INDUSTRY EXPERTISE */}

        <IndustryExpertise />

        {/* WHY CHOOSE US */}

        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-8 sm:py-10 lg:py-12'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />
          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-3xl mx-auto mb-4'>
              <span className='text-[#d32c30] font-extrabold uppercase tracking-[2px] text-xs'>
                The S Infinite Advantage
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57]'>
                Why Choose Our Express Delivery?
              </h2>

              <p className='text-gray-500 leading-7'>
                Reliable priority handling for urgent shipments.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-6'>
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.08
                    }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                      <Icon size={26} />
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-4'>
                      {benefit.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {benefit.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* DELIVERY PROCESS */}

        <section className='py-6 lg:py-6 bg-white'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-2xl mx-auto mb-7'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                How It Works
              </span>

              <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211f57] mt-2'>
                Our Express Delivery Process
              </h2>

              <p className='text-gray-500 mt-2 leading-7'>
                From booking to final delivery, we focus on a smooth and
                coordinated shipment journey.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative'>
              {process.map((step, index) => {
                const Icon = step.icon

                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1
                    }}
                    className='relative text-center'
                  >
                    <div className='w-16 h-16 mx-auto rounded-2xl bg-[#211f57] text-white flex items-center justify-center shadow-lg'>
                      <Icon size={27} />
                    </div>

                    <span className='inline-block text-xs font-extrabold text-[#d32c30] mt-4'>
                      STEP {step.number}
                    </span>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-2'>
                      {step.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-3'>
                      {step.description}
                    </p>

                    {index !== process.length - 1 && (
                      <div className='hidden lg:block absolute top-8 left-[calc(50%+45px)] w-[calc(100%-50px)] border-t-2 border-dashed border-[#d32c30]/30' />
                    )}
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}

        <section className='relative bg-[#211f57] py-7 overflow-hidden'>
          <div className='absolute right-0 top-0 w-96 h-96 rounded-full border-[50px] border-white/5 translate-x-1/3 -translate-y-1/3' />

          <div className='relative max-w-5xl mx-auto px-6 text-center'>
            <h2 className='text-2xl sm:text-3xl lg:text-3xl font-extrabold text-white leading-tight'>
              Looking for a Reliable
              <span className='block text-[#FFF44F]'>
                Express Delivery Partner?
              </span>
            </h2>

            <p className='text-white/70 max-w-4xl mx-auto leading-8'>
              S Infinite Express delivers fast, reliable logistics solutions for
              your urgent shipping needs.
            </p>
          </div>
        </section>

        <MoreServicesSection />

        <CTA />
      </main>
    </>
  )
}
